import React, { useEffect, useState } from 'react';
import SearchSelect from '../../components/SearchSelect';
import { selLayoutCls } from '../../utils/searchOptions';
import { SchemaGroupField, upsertDeviceExtValue } from '../../api/schema';
import {
    DictOptions,
    dictOptionLabel,
    useDictOptions,
} from '../../utils/dictOptions';

// 设备 Schema 字段表单公共层: DeviceCapabilityCard / DeviceSchemaGroups / DeviceCreateModal 共用,
// 保证三个编辑入口行为一致. 2026-08-14 store=column 机制移除:
// 全部字段统一读写 device_ext_value (整组覆盖), 不再按 store 分流到设备固定列.

export const CAPABILITY_GROUP = 'capability';

/** ext 字段值: 数值 / 字符串 / 字符串列表 (type=array, 如支持的夹具/附件 supportedFixtures) */
export type ExtFieldValue = number | string | boolean | string[];

/** 列表字段: 值是字符串数组, 表单里用逗号分隔录入 */
export const LIST_FIELD_TYPE = 'array';

/** 布尔字段 (2026-10-23, 如 FDM 打印机 / 工艺开关): 值存 JSON true/false, 录入是/否下拉. */
const BOOL_FIELD_TYPE = 'bool';
const STR_FIELD_TYPE = 'str';
const BOOL_OPTS = [
    { code: 'true', label: '是' },
    { code: 'false', label: '否' },
];

export function isListField(f: SchemaGroupField): boolean {
    return f.type === LIST_FIELD_TYPE;
}

/** 列表字段的表单文本 → 数组: 中英文逗号 / 换行分隔, 去空白与空项 */
export function splitListInput(v: string): string[] {
    return v
        .split(/[,，\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
}

/** 存量 ext 值 → 表单文本: 数组按逗号拼接 (与 splitListInput 互逆), null → 空串 */
export function extValueToInput(v: unknown): string {
    if (v == null) return '';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
}

/**
 * 字段组定义没写 type、但存量值是数组的字段, 按列表字段处理 —— 早期模板 (如 laser-frame capability 的
 * supportedFixtures) 只存了 key/label; 不补这一步, 数组会被当数字校验报错, 或在整组覆盖保存时被丢掉.
 */
export function withListTypes(
    fields: SchemaGroupField[],
    values: Record<string, unknown> | undefined,
): SchemaGroupField[] {
    if (!values) return fields;
    return fields.map((f) =>
        !f.type && Array.isArray(values[f.key]) ? { ...f, type: LIST_FIELD_TYPE } : f,
    );
}

export interface StorePatch {
    extPatch: Record<string, ExtFieldValue>;
    /** 首个数字校验不通过的字段, null=全部合法 (枚举/str/列表字段不校验) */
    invalid: { field: SchemaGroupField; raw: string } | null;
}

/** 表单字符串值 → ext patch: 空串=不设置 (键去掉); 列表字段存数组, 枚举/str 存字符串, 其余必须是数字. */
export function buildExtPatch(
    fields: SchemaGroupField[],
    raw: Record<string, string>,
): StorePatch {
    const extPatch: Record<string, ExtFieldValue> = {};
    for (const f of fields) {
        const v = (raw[f.key] ?? '').trim();
        if (v === '') continue;
        if (isListField(f)) {
            const list = splitListInput(v);
            if (list.length) extPatch[f.key] = list;
            continue;
        }
        if (f.type === BOOL_FIELD_TYPE) {
            extPatch[f.key] = v === 'true';
            continue;
        }
        // str 存原文 (多行 G-code 等首尾换行也是值的一部分), 只用 trim 判空
        let val: number | string = f.type === STR_FIELD_TYPE ? raw[f.key] : v;
        if (!f.options?.length && !f.optionsSource && f.type !== STR_FIELD_TYPE) {
            const n = Number(v);
            if (!Number.isFinite(n))
                return { extPatch, invalid: { field: f, raw: v } };
            val = n;
        }
        extPatch[f.key] = val;
    }
    return { extPatch, invalid: null };
}

/**
 * 表单字符串 → 套件里的类型化值 (设备向导用, 与 buildExtPatch 同口径, 2026-09-22):
 * 枚举 / 字典 / str 字段存字符串; 其余能转数字就存数字, 转不了保留原串 —— 让服务端预检去报
 * "应为数字", 而不是前端静默吞掉。此前向导把所有值按字符串写进 device_ext_value,
 * stdW 是字串时标定曲线"超标准功率"的校验会整段跳过。
 */
export function coerceSchemaFieldValue(
    field: SchemaGroupField,
    raw: string,
): ExtFieldValue {
    const v = raw.trim();
    if (isListField(field)) return splitListInput(v);
    if (field.type === BOOL_FIELD_TYPE && (v === 'true' || v === 'false')) return v === 'true';
    if (field.type === STR_FIELD_TYPE && !field.options?.length && !field.optionsSource)
        return v === '' ? v : raw;
    if (field.options?.length || field.optionsSource) return v;
    const n = Number(v);
    return v !== '' && Number.isFinite(n) ? n : v;
}

/** 保存 ext patch (整组覆盖). 返回后端 message (审批流提示). */
export async function saveExtPatch(
    deviceKey: string,
    groupKey: string,
    extPatch: Record<string, ExtFieldValue>,
): Promise<string> {
    const res = await upsertDeviceExtValue(
        deviceKey,
        groupKey,
        JSON.stringify(extPatch),
    );
    return res.message || '';
}

// 字典来源枚举取数已抽到 utils/dictOptions (与参数动态 Schema 表单共用); 这里保留 re-export.
export type { DictOptions };
export { useDictOptions };

/** 只读展示: 字典来源字段 code → label (不裸显 code), 列表逐项翻译后用顿号拼接, 其余原样; 空值由调用方处理. */
export function schemaFieldText(
    field: SchemaGroupField,
    value: unknown,
    dictOpts: DictOptions,
): string {
    if (Array.isArray(value))
        return value.map((v) => dictOptionLabel(dictOpts, field.optionsSource, String(v))).join('、');
    if (field.type === BOOL_FIELD_TYPE) {
        const s = extValueToInput(value);
        return s === 'true' ? '是' : s === 'false' ? '否' : s;
    }
    return dictOptionLabel(dictOpts, field.optionsSource, extValueToInput(value));
}

/**
 * 列表字段输入框: 逗号分隔录入. 自持草稿文本 —— 向导等调用方每次输入都会把值转成数组再转回串,
 * 直接受控会吞掉刚敲的分隔符 (「A,」→ ["A"] →「A」); 只在外部值与草稿解析结果不一致时才覆盖草稿.
 */
function ListFieldInput({
    value,
    onChange,
    className,
}: {
    value: string;
    onChange: (v: string) => void;
    className: string;
}) {
    const [draft, setDraft] = useState(value);
    useEffect(() => {
        setDraft((d) => (splitListInput(d).join(',') === splitListInput(value).join(',') ? d : value));
    }, [value]);
    return (
        <input
            className={className}
            value={draft}
            placeholder="多个用逗号分隔"
            onChange={(e) => {
                setDraft(e.target.value);
                onChange(e.target.value);
            }}
        />
    );
}

/** 单字段编辑控件: 枚举/字典字段 → 下拉, 列表字段 → 逗号分隔输入, 其余 → 文本输入. */
export function SchemaFieldInput({
    field,
    value,
    onChange,
    dictOpts,
    selectClassName = 'w-full text-sm',
    inputClassName = 'inp w-full text-sm',
}: {
    field: SchemaGroupField;
    value: string;
    onChange: (v: string) => void;
    dictOpts: DictOptions;
    selectClassName?: string;
    inputClassName?: string;
}) {
    if (isListField(field)) {
        return <ListFieldInput value={value} onChange={onChange} className={inputClassName} />;
    }
    const opts = field.type === BOOL_FIELD_TYPE
        ? BOOL_OPTS
        : field.optionsSource
          ? dictOpts[field.optionsSource] || []
          : (field.options || []).map((o) => ({ code: o, label: o }));
    if (opts.length || field.optionsSource) {
        return (
            <SearchSelect
                className={selLayoutCls(selectClassName)}
                value={value}
                onChange={(v) => onChange(v)}
                placeholder="未设置"
                options={opts.map((o) => ({ value: o.code, label: o.label }))}
                clearable
            />
        );
    }
    if (field.type === STR_FIELD_TYPE) {
        // 自由文本可能多行 (G-code / 流量补偿模型): 单行 input 会把换行吞成空格, 存回去就和原值不一致
        return (
            <textarea
                className={`${inputClassName} font-mono resize-y`}
                rows={Math.min(8, Math.max(1, value.split('\n').length))}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }
    return (
        <input
            className={inputClassName}
            value={value}
            onChange={(e) => onChange(e.target.value)}
        />
    );
}
